"use client";

import { Headset, RefreshCw, UserX } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { Button, List, Stack, Text } from "@leabankab/component-library";
import { StepContainer } from "@/components/layout";
import { Heading } from "@/components/ui";
import { type SupportedLocale, getLocaleConfig } from "@/config/locale-config";
import { SigningErrorIcon } from "./signing-error-icon";

type Props = {
	onTryAgain: () => void;
	/** A retry is already redirecting to the identity provider. */
	isRetrying?: boolean;
};

/**
 * FLOW answers 403 when the authenticated person is not the applicant. The
 * journey is recoverable with the right BankID, so it stays in the app.
 */
export const WrongUser = ({ onTryAgain, isRetrying = false }: Props) => {
	const t = useTranslations("pages.LoginPage.wrongUser");
	const locale = useLocale() as SupportedLocale;
	const { supportPhone: phone } = getLocaleConfig(locale);
	const whatYouCanDo = t.raw("whatYouCanDo") as string[];

	const actionIcons = [
		<RefreshCw key="retry" size={20} aria-hidden="true" />,
		<Headset key="support" size={20} aria-hidden="true" />,
	];

	return (
		<StepContainer>
			<Stack gap={3} align="stretch">
				<Stack gap={2} align="center" className="text-center text-balance">
					<SigningErrorIcon>
						<UserX size={40} strokeWidth={2} aria-hidden="true" />
					</SigningErrorIcon>
					<Heading level={1} textStyle="heading-lg">
						{t("title")}
					</Heading>
					<Text color="text.subtle">{t("description")}</Text>
				</Stack>

				<Stack gap={1.5}>
					<Heading level={2} textStyle="heading-xs">
						{t("whatYouCanDoTitle")}
					</Heading>
					<List.Root className="bg-transparent p-0">
						{whatYouCanDo.map((item, index) => (
							<List.Item key={item} icon={actionIcons[index]}>
								{item.replace("{phone}", phone)}
							</List.Item>
						))}
					</List.Root>
				</Stack>

				<Button
					size="lg"
					onClick={onTryAgain}
					disabled={isRetrying}
					aria-busy={isRetrying}
					className="w-full"
				>
					{t("tryAgain")}
				</Button>
			</Stack>
		</StepContainer>
	);
};
